import { Button, Css, Icon, Palette, RichTextField, useModal, useSnackbar, useTestIds } from "@homebound/beam";
import { useEffect, useRef, useState } from "react";
import { VersionableFragment } from "src/components/ReadyPlanVersionHeader.generated";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { useGenerateReadyPlanVersionNotesLazyQuery } from "./GenerateReadyPlanVersionNotes.generated";
import { useSaveReadyPlanAggregateVersionMutation } from "./PublishAggregateVersionModal.generated";

type PublishAggregateVersionModalProps = {
  copyText: string;
  draftAggregateVersion: VersionableFragment;
  readyPlanName: string;
  readyPlanId?: string;
  /**
   * When true, shows the "Generate with AI" helper which drafts version notes
   * based on detected changes in this draft.
   */
  allowGenerateAiNotes?: boolean;
};

export function PublishAggregateVersionModal(props: PublishAggregateVersionModalProps) {
  const { copyText, draftAggregateVersion, readyPlanName, readyPlanId, allowGenerateAiNotes } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [versionNotes, setVersionNotes] = useState<string | undefined>();
  const [hasGenerated, setHasGenerated] = useState(false);
  const [save] = useSaveReadyPlanAggregateVersionMutation();
  const [generateNotesQuery, { loading: generating }] = useGenerateReadyPlanVersionNotesLazyQuery({
    fetchPolicy: "network-only",
  });

  const { version } = draftAggregateVersion ?? {};

  // The AI helper needs a readyPlanId to query against, so only offer it when we have one.
  const canGenerateAiNotes = !!allowGenerateAiNotes && !!readyPlanId;

  async function publishPlanPackage() {
    await save({
      variables: { input: { id: draftAggregateVersion.id, status: AggregateVersionStatus.Active, versionNotes } },
    });
    triggerNotice({
      message: (
        <>
          v{version} of <span css={Css.tdu.$}>{readyPlanName}</span> has been published.
        </>
      ),
    });
    closeModal();
  }

  // Kick off the AI draft as soon as the modal opens so the user doesn't have to click "Generate"
  // (the button stays around as a "Regenerate" affordance). The ref guards against double-firing.
  const hasAutoGenerated = useRef(false);
  useEffect(() => {
    if (canGenerateAiNotes && !hasAutoGenerated.current) {
      hasAutoGenerated.current = true;
      void generateNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGenerateAiNotes]);

  async function generateNotes() {
    if (!readyPlanId) return;
    try {
      const { data } = await generateNotesQuery({ variables: { readyPlanId } });
      const notes = data?.generateReadyPlanVersionNotes.notes;
      // No notes (empty string) means we couldn't detect any changes worth summarizing, so let the user know.
      if (!notes) {
        triggerNotice({
          icon: "warning",
          message: "No changes were detected to summarize. You can write the notes manually.",
        });
        return;
      }

      setVersionNotes(notes);
      setHasGenerated(true);
    } catch {
      triggerNotice({
        icon: "error",
        message: "Could not generate version notes. Please try again or write them manually.",
      });
    }
  }

  return (
    <ConfirmationModal
      title={`Ready to Publish v${version}?`}
      label="Publish"
      onConfirmAction={() => publishPlanPackage()}
      confirmationMessage={
        <div css={Css.df.fdc.gap2.$}>
          <p>{copyText}</p>
          <VersionNotesField
            value={versionNotes}
            onChange={(val) => setVersionNotes(val)}
            allowGenerateAiNotes={canGenerateAiNotes}
            loading={generating}
            hasGenerated={hasGenerated}
            onGenerate={generateNotes}
            onClear={() => {
              setVersionNotes(undefined);
              setHasGenerated(false);
            }}
          />
        </div>
      }
    />
  );
}

type VersionNotesFieldProps = {
  value: string | undefined;
  onChange: (val: string | undefined) => void;
  allowGenerateAiNotes?: boolean;
  loading: boolean;
  hasGenerated: boolean;
  onGenerate: () => void;
  onClear: () => void;
};

function VersionNotesField(props: VersionNotesFieldProps) {
  const { value, onChange, allowGenerateAiNotes, loading, hasGenerated, onGenerate, onClear } = props;
  // Keep the field's stable "versionNotes" test id even though we render our own label.
  const tid = useTestIds(props, "versionNotes");
  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* Field label on the left, inline AI generate/regenerate action on the right. */}
      <div css={Css.df.aic.jcsb.$}>
        <span css={Css.sm.gray700.$}>Version Notes</span>
        {allowGenerateAiNotes && (
          <Button
            size="sm"
            variant="secondary"
            icon="star"
            label={loading ? "Generating…" : hasGenerated ? "Regenerate" : "Generate notes"}
            tooltip={
              hasGenerated
                ? "Replace the notes with a fresh AI summary of the changes detected in this draft."
                : "Summarize the changes detected in this draft into version notes you can edit before publishing."
            }
            disabled={loading}
            onClick={onGenerate}
          />
        )}
      </div>
      <RichTextField
        value={value}
        onChange={(val) => onChange(val)}
        placeholder={loading ? "Reviewing your draft and summarizing changes…" : undefined}
        {...tid}
      />
      {/* Once AI has drafted notes, label the content and offer a quick clear back to a blank field. */}
      {allowGenerateAiNotes && hasGenerated && !loading && (
        <div css={Css.df.aic.gap1.$}>
          <Icon icon="star" inc={2} color={Palette.Blue600} />
          <span css={Css.xs.gray600.$}>AI-drafted from detected changes — edit as needed.</span>
          <Button size="sm" variant="text" label="Clear" onClick={onClear} />
        </div>
      )}
    </div>
  );
}
